import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// Global "session kicked" callback — set by AuthProvider so the interceptor
// can flip the app into a kicked-out state and show the modal.
let _onSessionKicked = null;
export function setSessionKickedHandler(fn) { _onSessionKicked = fn; }

// Iter 157: global "account frozen" callback — the interceptor triggers
// this when the backend returns HTTP 423 so AuthProvider can update
// `user` with the fresh is_frozen=true payload, and RoleRoute swaps in
// the FrozenScreen without a full page reload.
let _onAccountFrozen = null;
export function setAccountFrozenHandler(fn) { _onAccountFrozen = fn; }

// Small helper the kicked-path uses to destroy cookies both client+server
// side so the modal doesn't re-fire on every subsequent API call.
async function _clearKickedCookies() {
  try { await axios.post(`${API}/auth/logout`); } catch {}
}

// Auto-refresh access token on a single 401, then retry the original request.
// Prevents "Not authenticated" after the 24h access-token lifetime expires
// without forcing the user to re-login.
let _refreshInflight = null;
axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/logout");

    // Hard session-kicked (another device signed in)  → no silent retry.
    // 1) Immediately clear server+client cookies so subsequent calls stop
    //    triggering 440 in a loop.
    // 2) If we're already on the login page, swallow silently — the user is
    //    exactly where they need to be, no modal needed.
    // 3) Otherwise fire the global handler so the app shows the lockout modal.
    if (status === 440 || error.response?.data?.detail === "SESSION_KICKED") {
      await _clearKickedCookies();
      const onLoginPage = typeof window !== "undefined" &&
        (window.location.pathname === "/login" ||
         window.location.pathname.startsWith("/forgot-password") ||
         window.location.pathname.startsWith("/reset-password"));
      if (!onLoginPage) {
        try { _onSessionKicked && _onSessionKicked(); } catch { /* noop */ }
      }
      return Promise.reject(error);
    }

    // Account disabled OR forced logout → no refresh retry. Clear cookies and
    // bounce to login. The user must enter their password again to come back
    // in (and if the account is genuinely disabled, even that won't work).
    const detail = error.response?.data?.detail;
    const detailCode = detail && typeof detail === "object" ? detail.code : null;
    const isDisabled = status === 403 && detail === "ACCOUNT_DISABLED";
    const isForced  = status === 401 && detail === "FORCED_LOGOUT";
    // Iter 157: HTTP 423 ACCOUNT_FROZEN — every authenticated endpoint
    // except the whitelisted identity/logout/frozen-info ones will
    // return this while the student's account is frozen. We refresh
    // /me so `user.is_frozen` flips to true and RoleRoute renders the
    // FrozenScreen. Do NOT clear cookies — the student needs to stay
    // logged in so the frozen screen has an identity to display.
    const isFrozen = status === 423 || detailCode === "ACCOUNT_FROZEN";
    if (isFrozen) {
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        _onAccountFrozen && _onAccountFrozen(data);
      } catch { /* if /me itself fails, fall through */ }
      return Promise.reject(error);
    }
    if (isDisabled || isForced) {
      await _clearKickedCookies();
      const onLoginPage = typeof window !== "undefined" && window.location.pathname === "/login";
      if (!onLoginPage && typeof window !== "undefined") {
        const next = isDisabled
          ? "/login?reason=account_disabled"
          : "/login?reason=forced_logout";
        window.location.replace(next);
      }
      return Promise.reject(error);
    }

    if (status === 401 && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      try {
        _refreshInflight = _refreshInflight || axios.post(`${API}/auth/refresh`);
        await _refreshInflight;
        _refreshInflight = null;
        return axios(original);
      } catch (e) {
        _refreshInflight = null;
        // If refresh itself returned 440, fire the kicked handler
        if (e?.response?.status === 440) {
          await _clearKickedCookies();
          const onLoginPage = typeof window !== "undefined" &&
            window.location.pathname === "/login";
          if (!onLoginPage) {
            try { _onSessionKicked && _onSessionKicked(); } catch {}
          }
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  if (typeof detail === "object") { try { return JSON.stringify(detail); } catch { return "Request failed."; } }
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const [checking, setChecking] = useState(true);
  const [sessionKicked, setSessionKicked] = useState(false);

  // Register the global 440 handler so the axios interceptor can trigger the
  // kicked-out modal without importing React at module-load time.
  useEffect(() => {
    setSessionKickedHandler(() => {
      setSessionKicked(true);
      setUser(false);
    });
    // Iter 157: register the frozen handler too. When the interceptor
    // catches a 423, it re-fetches /me and hands us the enriched user
    // object (with is_frozen=true) so RoleRoute can swap to FrozenScreen.
    setAccountFrozenHandler((data) => {
      if (data && typeof data === "object") setUser(data);
    });
    return () => {
      setSessionKickedHandler(null);
      setAccountFrozenHandler(null);
    };
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  // Heartbeat: when authenticated, poll /api/security/heartbeat every 90s so
  // a session kicked by another device is surfaced as the lockout modal
  // within seconds — without waiting for the user to click anything. Also
  // re-check immediately whenever the tab/window regains focus (common mobile
  // flow: user switches apps, comes back, should see kicked state instantly).
  useEffect(() => {
    if (!user || typeof user === "boolean") return undefined;
    if (user.role !== "student" && user.role !== "mentor") return undefined;

    let cancelled = false;
    const ping = async () => {
      if (cancelled) return;
      try {
        await axios.get(`${API}/security/heartbeat`);
      } catch {
        // 440 is handled globally by the axios interceptor → fires
        // SessionKickedModal automatically. Nothing to do here.
      }
    };

    const interval = setInterval(() => { if (document.hidden) return; ping(); }, 90000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") ping();
    };
    document.addEventListener("visibilitychange", onVisibility);
    // Also ping immediately on mount (catches kicks that happened while the
    // tab was closed / in the background).
    ping();

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    // OTP-challenge response is HTTP 200 with `{otp_required: true, ...}` —
    // it is NOT a logged-in user. Do not store it in `user` state, otherwise
    // RoleRoute sees a truthy-but-roleless object and bounces students into
    // a redirect loop that paints a blank navy screen. Caller must inspect
    // the returned payload and drive the OTP flow.
    if (!data?.otp_required) {
      setUser(data);
      setSessionKicked(false);
    }
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {}
    setUser(false);
    setSessionKicked(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, checking, login, logout, refreshMe, sessionKicked, setSessionKicked }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}

export { API };
